import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { 
  ShieldCheck, ArrowLeft, Lock, Download, 
  Mail, CheckCircle2, FileText, Scale, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreviewPage = () => {
  const { letterId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Check if returning from PayPal
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');

  const fetchLetter = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/letters/${letterId}/preview`);
      setLetter(response.data);
      setEmail(response.data.tenant_email || "");
    } catch (error) {
      console.error("Failed to fetch letter:", error);
      toast.error("Failed to load letter preview");
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [letterId, navigate]);

  const executePayment = useCallback(async () => {
    if (!paymentId || !payerId) return;
    
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API}/payments/execute`, null, {
        params: { payment_id: paymentId, payer_id: payerId }
      });
      
      if (response.data.success) {
        toast.success("Payment successful!");
        navigate(`/success?letter_id=${letterId}`);
      }
    } catch (error) {
      console.error("Payment execution failed:", error);
      toast.error("Payment processing failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  }, [paymentId, payerId, letterId, navigate]);

  useEffect(() => {
    fetchLetter();
  }, [fetchLetter]);

  useEffect(() => {
    if (paymentId && payerId) {
      executePayment();
    }
  }, [paymentId, payerId, executePayment]);

  const handleCreateOrder = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return null;
    }

    try {
      const response = await axios.post(`${API}/payments/create-order`, {
        letter_id: letterId,
        email: email
      });
      
      // For PayPal REST SDK, we redirect to approval URL
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
      
      return response.data.payment_id;
    } catch (error) {
      console.error("Failed to create order:", error);
      toast.error("Failed to initialize payment");
      return null;
    }
  };

  // Dev mode: Mark as paid for testing
  const handleDevPay = async () => {
    try {
      await axios.post(`${API}/dev/mark-paid/${letterId}`);
      toast.success("Letter marked as paid (dev mode)");
      navigate(`/success?letter_id=${letterId}`);
    } catch (error) {
      toast.error("Failed to mark as paid");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-600">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (paymentLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-600">Processing payment...</p>
        </div>
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-600">Letter not found</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  // If already paid, redirect to success
  if (letter.paid) {
    navigate(`/success?letter_id=${letterId}`);
    return null;
  }

  // Split letter content for partial blur effect
  const letterLines = letter.letter_content?.split('\n') || [];
  const headerLines = letterLines.slice(0, 12);
  const bodyLines = letterLines.slice(12, -8);
  const footerLines = letterLines.slice(-8);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/create')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              data-testid="back-to-form-btn"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="hidden sm:inline">Edit Details</span>
            </button>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-blue-600" />
              <span className="font-serif text-lg font-bold text-slate-900">TenantShield</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Letter Preview */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-blue-600" />
                <h2 className="font-serif text-xl font-bold text-slate-900">
                  Your Demand Letter Preview
                </h2>
              </div>

              <div className="bg-white border border-slate-200 shadow-lg paper-texture relative overflow-hidden">
                {/* Watermark */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-6xl font-serif font-bold text-blue-600/10 rotate-[-45deg] whitespace-nowrap">
                    PREVIEW
                  </span>
                </div>

                <div className="p-8 md:p-12 relative">
                  {/* Header - Clear */}
                  <pre className="letter-document text-slate-800 whitespace-pre-wrap">
                    {headerLines.join('\n')}
                  </pre>

                  {/* Body - Blurred */}
                  <div className="relative my-4">
                    <pre className="letter-document text-slate-800 whitespace-pre-wrap letter-blur">
                      {bodyLines.join('\n')}
                    </pre>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/80 backdrop-blur-sm px-6 py-3 rounded-lg shadow-lg border border-slate-200">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Lock className="h-5 w-5 text-blue-600" />
                          <span className="font-semibold">Pay to unlock full letter</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer - Clear */}
                  <pre className="letter-document text-slate-800 whitespace-pre-wrap">
                    {footerLines.join('\n')}
                  </pre>
                </div>
              </div>

              {/* Legal Info */}
              <div className="mt-4 flex items-start gap-3 text-sm text-slate-600">
                <Scale className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p>
                  This letter cites <strong>{letter.state}</strong> security deposit law. 
                  Landlords are required to return deposits within the statutory period or 
                  provide itemized deductions.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Payment Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white border border-slate-200 shadow-lg p-6 sticky top-24"
            >
              <h3 className="font-serif text-xl font-bold text-slate-900 mb-4">
                Unlock Your Letter
              </h3>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Security Deposit Demand Letter</span>
                  <span className="font-mono font-bold">$29.00</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    State-specific legal citations
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Instant PDF download
                  </div>
{/* Removed email option */}
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Print-ready formatting
                  </div>
                </div>
              </div>

              {/* Email Input */}
              <div className="mb-6">
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Email for delivery
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="payment-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="h-12"
                />
              </div>

              {/* PayPal Button */}
              <div className="space-y-4">
                <Button
                  onClick={handleCreateOrder}
                  data-testid="pay-with-paypal-btn"
                  className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white py-6 text-lg font-semibold"
                >
                  Pay with PayPal - $29
                </Button>

{/* Production mode - no dev button */}
              </div>

              {/* Trust Indicators */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-1">
                    <Lock className="h-4 w-4" />
                    Secure Payment
                  </div>
                  <div className="flex items-center gap-1">
                    <ShieldCheck className="h-4 w-4" />
                    SSL Encrypted
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PreviewPage;
